import { FC } from 'react';
import { TApiError } from '@mybonum/api';
import { CustomModal } from '@mybonum/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Flex, Typography } from 'antd';

import { TDiscoverNewClinicsModalProps } from './types.ts';

import { useMessage } from '@/hooks/use-message.ts';
import { API } from '@/services/api';

export const DiscoverNewClinicsModal: FC<TDiscoverNewClinicsModalProps> = ({ open, onClose }) => {
    const queryClient = useQueryClient();
    const { message } = useMessage();

    const { mutateAsync: updateInfoAll, isPending } = useMutation({
        mutationFn: API.PRACTICES.updateInfoAll,
    });

    const handleRunDiscovery = async (): Promise<void> => {
        try {
            const data = await updateInfoAll();
            await queryClient.invalidateQueries({
                queryKey: API.PRACTICES.getPractices({}).queryKey,
            });
            onClose();
            message.success(data.message);
        } catch (error) {
            const apiError = error as TApiError;
            const responseData = apiError.response?.data;
            const errorMessage =
                responseData?.error || apiError.message || 'Something went wrong...';
            await message.error(errorMessage);
        }
    };

    return (
        <CustomModal
            width={360}
            open={open}
            padding="small"
            onCancel={onClose}
            title="Discover New Clinics?"
            destroyOnHidden
            footer={
                <Flex gap={16} justify="space-between">
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" onClick={handleRunDiscovery} loading={isPending}>
                        Run discovery
                    </Button>
                </Flex>
            }
        >
            <Typography style={{ color: 'var(--color-brand-info)', textAlign: 'center' }}>
                This will run the weekly clinic discovery process and search for new practices
                across connected sources. This action may take several minutes.
            </Typography>
        </CustomModal>
    );
};
